/**
 * Gateway-owned egress policy objects (#2487): an ordered list of
 * `ingress region → upstream selector → via` rules, assigned per org,
 * inherited over a single global default.
 *
 * Matching is pure and identical for the dispatcher and the operator
 * preview. Specificity (highest first):
 *
 *   (ingress exact, upstream_id)
 *   (ingress exact, upstream_region)
 *   (ingress exact, *)
 *   (*, upstream_id)
 *   (*, upstream_region)
 *   (*, *)
 *
 * Ties inside a rank are broken by rule `position` (lower first). The
 * org policy is the overlay; the global default is the floor. Either
 * policy's `emergency_direct` forces DIRECT and skips matching.
 *
 * An unknown upstream region matches only `*` and `upstream_id` rules —
 * a `{region}` selector cannot fire on a missing label.
 */

import { isValidEgressLinkRegion } from './internal-egress-links.ts';
import type { GatewayMutationReceipt } from './gateway-mutation-receipts.ts';

export const GLOBAL_DEFAULT_EGRESS_POLICY_ID = 'egp_global_default';
export const EGRESS_POLICY_RECEIPT_KIND = 'egress_policy';

export const EGRESS_VIA_KINDS = ['direct', 'cluster'] as const;
export type EgressViaKind = (typeof EGRESS_VIA_KINDS)[number];

export const EGRESS_FALLBACKS = ['direct', 'none'] as const;
export type EgressFallback = (typeof EGRESS_FALLBACKS)[number];

export const EGRESS_RULE_SPECIFICITY_RANKS = [
  'ingress_exact+upstream_id',
  'ingress_exact+upstream_region',
  'ingress_exact+*',
  '*+upstream_id',
  '*+upstream_region',
  '*+*',
] as const;
export type EgressRuleSpecificityRank = (typeof EGRESS_RULE_SPECIFICITY_RANKS)[number];

export const EGRESS_MEMBER_ELIGIBILITY_REASONS = [
  'ok',
  'standby',
  'stale_heartbeat',
  'breaker_open',
  'probe_failing',
  'same_region',
] as const;
export type EgressMemberEligibilityReason = (typeof EGRESS_MEMBER_ELIGIBILITY_REASONS)[number];

export type EgressRuleVia =
  | { readonly kind: 'direct' }
  | { readonly kind: 'cluster'; readonly cluster: string };

export interface EgressPolicyRuleRecord {
  readonly id: string;
  readonly policy_id: string;
  readonly position: number;
  readonly ingress_region: string;
  readonly upstream_id: string | null;
  readonly upstream_region: string | null;
  readonly via_kind: EgressViaKind;
  readonly via_cluster: string | null;
  readonly fallback: EgressFallback;
  readonly note: string | null;
}

export interface EgressPolicyRecord {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly is_global_default: boolean;
  readonly emergency_direct: boolean;
  readonly version: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly assigned_org_count: number;
  readonly rules: readonly EgressPolicyRuleRecord[];
}

export interface EgressPolicyAssignmentRecord {
  readonly org_id: string;
  readonly policy_id: string;
  readonly created_at: string;
  readonly updated_at: string;
  /**
   * Ops-api list enrichment only (#2512). Gateway `/internal/egress-policies`
   * does not set this; `GET /v1/ops/egress/policies` joins the customer
   * projection and adds it additively (`null` when the org has no email).
   */
  readonly email?: string | null;
}

export interface EgressPolicyRuleInput {
  readonly ingress_region: string;
  readonly upstream_id?: string | null;
  readonly upstream_region?: string | null;
  readonly via_kind: EgressViaKind;
  readonly via_cluster?: string | null;
  readonly fallback?: EgressFallback;
  readonly note?: string | null;
}

export interface CreateEgressPolicyRequest {
  readonly name: string;
  readonly description?: string | null;
  readonly emergency_direct?: boolean;
  readonly rules: readonly EgressPolicyRuleInput[];
}

export interface UpdateEgressPolicyRequest {
  readonly name: string;
  readonly description?: string | null;
  readonly emergency_direct?: boolean;
  readonly expected_version: number;
  readonly rules: readonly EgressPolicyRuleInput[];
}

export interface ListEgressPoliciesResponse {
  readonly policies: readonly EgressPolicyRecord[];
  /** Org assignments across every policy. Additive for the operator UI. */
  readonly assignments: readonly EgressPolicyAssignmentRecord[];
}

export interface GetEgressPolicyResponse {
  readonly policy: EgressPolicyRecord;
}

export interface MutateEgressPolicyResponse {
  readonly policy: EgressPolicyRecord;
  readonly mutation_receipt: GatewayMutationReceipt;
}

export interface DeleteEgressPolicyResponse {
  readonly deleted: true;
  readonly mutation_receipt: GatewayMutationReceipt;
}

export interface GetOrgEgressPolicyResponse {
  readonly assignment: EgressPolicyAssignmentRecord | null;
  readonly policy: EgressPolicyRecord | null;
}

export interface PutOrgEgressPolicyRequest {
  readonly policy_id: string | null;
}

export interface PutOrgEgressPolicyResponse {
  readonly assignment: EgressPolicyAssignmentRecord | null;
  readonly mutation_receipt: GatewayMutationReceipt;
}

export interface ResolvedEgressRule {
  readonly via: EgressRuleVia;
  readonly fallback: EgressFallback;
  readonly policyId: string;
  readonly ruleIndex: number | null;
  readonly inheritedFromGlobal: boolean;
  readonly emergencyDirect: boolean;
}

export interface EgressClusterMemberEligibility {
  readonly egress_id: string;
  readonly region: string;
  readonly eligible: boolean;
  readonly reason: EgressMemberEligibilityReason;
}

export interface EffectiveEgressPolicyResponse {
  readonly via: EgressRuleVia;
  readonly fallback: EgressFallback;
  readonly policy_id: string;
  readonly rule_index: number | null;
  readonly inherited_from_global: boolean;
  readonly emergency_direct: boolean;
  readonly policy_version: number | null;
  readonly cached_at: string | null;
  readonly members: readonly EgressClusterMemberEligibility[];
  /** Dispatch path this node would use (`direct` or an `egress_id`). */
  readonly selected_path_id: string;
  readonly selected_reason:
    | 'policy_direct'
    | 'policy_cluster'
    | 'emergency_direct'
    | 'cluster_down'
    | 'token_unset';
  /** True when the matched cluster has no eligible member (or CONNECT-exhausted). */
  readonly cluster_down: boolean;
}

export interface ResolveEgressRuleInput {
  readonly ingressRegion: string;
  readonly upstreamId: string;
  readonly upstreamRegion: string | null;
  readonly orgPolicy: EgressPolicyRecord | null;
  readonly globalPolicy: EgressPolicyRecord | null;
}

const WILDCARD = '*';

export function isEgressViaKind(value: unknown): value is EgressViaKind {
  return value === 'direct' || value === 'cluster';
}

export function isEgressFallback(value: unknown): value is EgressFallback {
  return value === 'direct' || value === 'none';
}

export function isEgressIngressSelector(value: unknown): value is string {
  return value === WILDCARD || isValidEgressLinkRegion(value);
}

export function validateEgressPolicyRuleInput(rule: EgressPolicyRuleInput): string | null {
  if (!isEgressIngressSelector(rule.ingress_region)) {
    return 'ingress_region must be a lowercase slug or *';
  }
  const upstreamId = emptyToNull(rule.upstream_id);
  const upstreamRegion = emptyToNull(rule.upstream_region);
  if (upstreamId !== null && upstreamRegion !== null) {
    return 'at most one of upstream_id and upstream_region may be set';
  }
  if (upstreamRegion !== null && !isValidEgressLinkRegion(upstreamRegion)) {
    return 'upstream_region must be a lowercase slug';
  }
  if (!isEgressViaKind(rule.via_kind)) {
    return "via_kind must be 'direct' or 'cluster'";
  }
  const viaCluster = emptyToNull(rule.via_cluster);
  if (rule.via_kind === 'cluster') {
    if (viaCluster === null || !isValidEgressLinkRegion(viaCluster)) {
      return 'via_cluster is required when via_kind is cluster';
    }
    if (rule.ingress_region !== WILDCARD && viaCluster === rule.ingress_region) {
      return 'via_cluster cannot equal ingress_region (same-region exit is excluded)';
    }
  } else if (viaCluster !== null) {
    return 'via_cluster must be null when via_kind is direct';
  }
  if (rule.fallback !== undefined && !isEgressFallback(rule.fallback)) {
    return "fallback must be 'direct' or 'none'";
  }
  return null;
}

export function resolveEgressRule(input: ResolveEgressRuleInput): ResolvedEgressRule {
  const orgEmergency = input.orgPolicy?.emergency_direct === true;
  const globalEmergency = input.globalPolicy?.emergency_direct === true;
  if (orgEmergency || globalEmergency) {
    const policy = orgEmergency ? input.orgPolicy : input.globalPolicy;
    return {
      via: { kind: 'direct' },
      fallback: 'direct',
      policyId: policy?.id ?? '',
      ruleIndex: null,
      inheritedFromGlobal: !orgEmergency,
      emergencyDirect: true,
    };
  }

  const orgHit = input.orgPolicy === null
    ? null
    : pickMatchingRule(input.orgPolicy, input);
  if (orgHit !== null) {
    return toResolved(orgHit, input.orgPolicy!.id, false);
  }

  const globalHit = input.globalPolicy === null
    ? null
    : pickMatchingRule(input.globalPolicy, input);
  if (globalHit !== null) {
    return toResolved(globalHit, input.globalPolicy!.id, true);
  }

  return {
    via: { kind: 'direct' },
    fallback: 'direct',
    policyId: input.globalPolicy?.id ?? '',
    ruleIndex: null,
    inheritedFromGlobal: true,
    emergencyDirect: false,
  };
}

function pickMatchingRule(
  policy: EgressPolicyRecord,
  input: ResolveEgressRuleInput,
): EgressPolicyRuleRecord | null {
  const ingress = normalizeSlug(input.ingressRegion);
  const upstreamId = input.upstreamId;
  const upstreamRegion = input.upstreamRegion === null ? null : normalizeSlug(input.upstreamRegion);

  let best: { rank: number; position: number; rule: EgressPolicyRuleRecord } | null = null;
  for (const rule of policy.rules) {
    const rank = specificityRank(rule, ingress, upstreamId, upstreamRegion);
    if (rank === null) continue;
    if (
      best === null
      || rank < best.rank
      || (rank === best.rank && rule.position < best.position)
    ) {
      best = { rank, position: rule.position, rule };
    }
  }
  return best?.rule ?? null;
}

function specificityRank(
  rule: EgressPolicyRuleRecord,
  ingress: string,
  upstreamId: string,
  upstreamRegion: string | null,
): number | null {
  const ruleIngress = normalizeSlug(rule.ingress_region);
  const ingressExact = ruleIngress !== WILDCARD && ruleIngress === ingress;
  const ingressStar = ruleIngress === WILDCARD;
  if (!ingressExact && !ingressStar) return null;

  const hasId = rule.upstream_id !== null && rule.upstream_id.length > 0;
  const hasRegion = rule.upstream_region !== null && rule.upstream_region.length > 0;
  if (hasId && hasRegion) return null;

  if (hasId && rule.upstream_id !== upstreamId) return null;
  if (hasRegion) {
    if (upstreamRegion === null) return null;
    if (normalizeSlug(rule.upstream_region ?? '') !== upstreamRegion) return null;
  }

  if (ingressExact && hasId) return 0;
  if (ingressExact && hasRegion) return 1;
  if (ingressExact && !hasId && !hasRegion) return 2;
  if (ingressStar && hasId) return 3;
  if (ingressStar && hasRegion) return 4;
  if (ingressStar && !hasId && !hasRegion) return 5;
  return null;
}

function toResolved(
  rule: EgressPolicyRuleRecord,
  policyId: string,
  inheritedFromGlobal: boolean,
): ResolvedEgressRule {
  const via: EgressRuleVia = rule.via_kind === 'cluster' && rule.via_cluster !== null
    ? { kind: 'cluster', cluster: rule.via_cluster }
    : { kind: 'direct' };
  return {
    via,
    fallback: rule.fallback,
    policyId,
    ruleIndex: rule.position,
    inheritedFromGlobal,
    emergencyDirect: false,
  };
}

function normalizeSlug(value: string): string {
  return value.trim().toLowerCase();
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
